import React, { useState, useEffect } from "react";
import api from "../../api/api";
import { useTranslation } from "react-i18next";

const AddressForm = ({ onSave, onCancel, addressToEdit }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    address_type: "shipping",
    address_line_1: "",
    address_line_2: "",
    city: "",
    state_province: "",
    postal_code: "",
    country: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // If an address is passed, pre-fill the form for editing
    if (addressToEdit) {
      setFormData({
        address_type: addressToEdit.address_type,
        address_line_1: addressToEdit.address_line_1,
        address_line_2: addressToEdit.address_line_2 || "",
        city: addressToEdit.city,
        state_province: addressToEdit.state_province,
        postal_code: addressToEdit.postal_code,
        country: addressToEdit.country,
      });
    }
  }, [addressToEdit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (addressToEdit) {
        // Update existing address
        await api.put(`/user/addresses/${addressToEdit.address_id}`, formData);
      } else {
        // Create new address
        await api.post("/user/addresses", formData);
      }
      onSave(); // Notify parent component to refresh addresses and close the form
    } catch (err) {
      console.error("Error saving address:", err);
      setError(t("address.form.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white p-8 rounded-lg shadow-lg max-w-lg mx-auto mt-8">
      <h2 className="text-2xl font-bold mb-6 text-gray-800">
        {addressToEdit
          ? t("address.form.editTitle")
          : t("address.form.addTitle")}
      </h2>
      <form onSubmit={handleSubmit}>
        {error && <div className="text-red-500 mb-4">{error}</div>}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="mb-4">
            <label className="block text-gray-700">
              {t("address.form.type")}
            </label>
            <select
              name="address_type"
              value={formData.address_type}
              onChange={handleChange}
              className="mt-1 p-2 w-full border rounded-md"
            >
              <option value="shipping">
                {t("address.form.typeOptions.shipping")}
              </option>
              <option value="billing">
                {t("address.form.typeOptions.billing")}
              </option>
              <option value="both">{t("address.form.typeOptions.both")}</option>
            </select>
          </div>
          <div className="mb-4">
            <label className="block text-gray-700">
              {t("address.form.line1")}
            </label>
            <input
              type="text"
              name="address_line_1"
              value={formData.address_line_1}
              onChange={handleChange}
              className="mt-1 p-2 w-full border rounded-md"
              required
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700">
              {t("address.form.line2")}
            </label>
            <input
              type="text"
              name="address_line_2"
              value={formData.address_line_2}
              onChange={handleChange}
              className="mt-1 p-2 w-full border rounded-md"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700">
              {t("address.form.city")}
            </label>
            <input
              type="text"
              name="city"
              value={formData.city}
              onChange={handleChange}
              className="mt-1 p-2 w-full border rounded-md"
              required
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700">
              {t("address.form.state")}
            </label>
            <input
              type="text"
              name="state_province"
              value={formData.state_province}
              onChange={handleChange}
              className="mt-1 p-2 w-full border rounded-md"
              required
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700">
              {t("address.form.postal")}
            </label>
            <input
              type="text"
              name="postal_code"
              value={formData.postal_code}
              onChange={handleChange}
              className="mt-1 p-2 w-full border rounded-md"
              required
            />
          </div>
          <div className="mb-4 col-span-1 md:col-span-2">
            <label className="block text-gray-700">
              {t("address.form.country")}
            </label>
            <input
              type="text"
              name="country"
              value={formData.country}
              onChange={handleChange}
              className="mt-1 p-2 w-full border rounded-md"
              required
            />
          </div>
        </div>
        <div className="flex justify-end space-x-4 mt-6">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-100"
            disabled={loading}
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-blue-300"
            disabled={loading}
          >
            {loading ? t("common.saving") : t("address.form.save")}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddressForm;
